import i18n from '@/plugins/i18n'
import store from '@/store'

export const colors = [
  '#D13D4B',
  '#DD644C',
  '#EB8547',
  '#F4A641',
  '#FFC73C',
  '#F9E03B',
  '#F9E03B',
  '#BBD665',
  '#9FD17C',
  '#86CC8E'
]

function getColorForValue(value: number, max: number, reverse = false, colorRange: string[] = colors) {
  let percentage = value / max

  if (percentage > 1) percentage = 1

  let index = Math.floor(percentage * 10)

  if (index === 0) index = 1

  if (reverse) {
    return colorRange[colorRange.length - index]
  }

  return colorRange[index - 1]
}

interface ColorValueDefinition {
  max: number,
  color: string
}

function getColorForValueWithDefinition(value: number, definition: ColorValueDefinition[]) {
  if (!value) value = 0

  return definition.find(({ max }) => value <= max)?.color
}

function processMaxValues(stats: any[], factor: number = 1.3) {
  if (!stats) return {}

  const maxValues: any = {}

  for (const stat of stats) {
    if (!stat) continue

    const keys = Object.keys(stat)

    for (const key of keys) {
      maxValues[key] = (!maxValues[key] || stat[key] * factor > maxValues[key]) ? stat[key] * factor : maxValues[key]
    }
  }

  return maxValues
}

function processMinValues(stats: any[]) {
  if (!stats) return {}

  const minValues: any = {}

  for (const stat of stats) {
    if (!stat) continue

    const keys = Object.keys(stat)

    for (const key of keys) {
      minValues[key] = (!minValues[key] || stat[key] < minValues[key]) ? stat[key] : minValues[key]
    }
  }

  return minValues
}

function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min) + min)
}

function toCamelCase(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
}

function getOrdinal(num: number) {
  if (num % 100 >= 11 && num % 100 <= 13) {
    return i18n.t('ordinal.1', { num: num })
  }

  switch (num % 10) {
    case 1:
      return i18n.t('ordinal.1', { num: num })
    case 2:
      return i18n.t('ordinal.2', { num: num })
    case 3:
      return i18n.t('ordinal.3', { num: num })
    default:
      return i18n.t('ordinal.other', { num: num })
  }
}

function calculateDistribution(
  players: any[],
  statVariable: string,
  min: number = 1,
  max: number = 100,
  step: number = 10,
  extraBinForMax: boolean = true
) {
  const numBins = Math.ceil((max - min) / step)

  const bins = Array(numBins).fill(0).map((_, index) => {
    const binStart = min + (index * step)
    const binEnd = Math.min(binStart + step - 1, max - 1) // Ensure last bin doesn't overlap with max
    return {
      key: step > 1 ? `${binStart}-${binEnd}` : `${binStart}`,
      value: 0
    }
  })

  if (extraBinForMax) {
    bins.push({ key: `${max}+`, value: 0 })
  }

  for (const player of players) {
    if (!player.player_stats || typeof player.player_stats[statVariable] !== 'number') {
      continue
    }

    const value = player.player_stats[statVariable]

    if (extraBinForMax && value >= max) {
      bins[bins.length - 1].value++
      continue
    }

    if (value >= min && value < max) {
      const binIndex = Math.floor((value - min) / step)
      if (binIndex >= 0 && binIndex < numBins) {
        bins[binIndex].value++
      }
    }
  }

  return bins
}

export function calculateDistributionFromCounts(
  dist: Array<{ value: number | string; count: number | string }>,
  min: number = 1,
  max: number = 10,
  step: number = 1
) {
  const map = new Map<number, number>()
  for (const item of dist) {
    const k = Number((item as any).value)
    const v = Number((item as any).count) || 0
    if (Number.isFinite(k)) {
      // accumulate in case of duplicates
      map.set(k, (map.get(k) || 0) + v)
    }
  }

  // When step = 1, preserve original behavior: each integer key in [min, max]
  if (step <= 1) {
    const full: Array<{ key: number; value: number }> = []
    for (let g = min; g <= max; g++) {
      full.push({ key: g, value: map.get(g) || 0 })
    }
    return full
  }

  // When step > 1, aggregate into ranges and output range labels as keys, e.g. "1-10"
  const ranged: Array<{ key: string; value: number }> = []
  for (let start = min; start <= max; start += step) {
    const end = Math.min(start + step - 1, max)
    let sum = 0
    for (let val = start; val <= end; val++) {
      sum += map.get(val) || 0
    }
    ranged.push({ key: `${start}-${end}`, value: sum })
  }
  return ranged
}

function splitVariable(variable: string) {
  return variable.split('_')
}

function adjustGridForTableHeights(
  grid: any[] = [],
  tableRowCount: number = 0,
  diagnosticTableRowCount: number = 0,
  rowHeightPx: number = 52,
  headerPx: number = 130
) {
  if (!grid || !Array.isArray(grid)) return []

  const tableElement = grid.find(element => element.type === 'table')
  const diagnosticTableElement = grid.find(element => element.type === 'diagnostic_table')

  let tableHeightDiff = 0
  if (tableElement && tableRowCount > 0) {
    const itemsLength = tableRowCount || 5
    const predictedHeight = Math.ceil((itemsLength * rowHeightPx + headerPx) / 80)
    tableHeightDiff = predictedHeight - tableElement.h
  }

  let diagnosticHeightDiff = 0
  if (diagnosticTableElement && diagnosticTableRowCount > 0) {
    const itemsLength = diagnosticTableRowCount || 5
    const predictedHeight = Math.ceil((itemsLength * 52 + 130) / 80)
    diagnosticHeightDiff = predictedHeight - diagnosticTableElement.h
  }

  const sortedElements = [...grid].sort((a, b) => a.y - b.y)

  return sortedElements.map(element => {
    const result = { ...element }

    if (element.type === 'table') {
      result.h = element.h + tableHeightDiff
    } else if (element.type === 'diagnostic_table') {
      result.h = element.h + diagnosticHeightDiff

      if (tableElement && tableElement.y < element.y) {
        result.y = element.y + tableHeightDiff
      }
    } else {
      let yOffset = 0

      if (tableElement && tableElement.y < element.y) {
        yOffset += tableHeightDiff
      }

      if (diagnosticTableElement) {
        const diagnosticTableFinalY = diagnosticTableElement.y +
          (tableElement && tableElement.y < diagnosticTableElement.y ? tableHeightDiff : 0)

        if (diagnosticTableFinalY < element.y) {
          yOffset += diagnosticHeightDiff
        }
      }

      result.y = element.y + yOffset
    }

    return result
  })
}

const dateTimeFormatMap: { [key: string]: string } = {
  'HH:mm': 'h:mm A',
  'HH:mm:ss': 'h:mm:ss A',
  'ddd, L - HH:mm': 'ddd, L - h:mm A',
  'ddd, MMM D, HH:mm': 'ddd, MMM D, h:mm A',
  'ddd, MMMM D, HH:mm': 'ddd, MMMM D, h:mm A',
  'dddd, ll HH:mm:ss': 'dddd, ll h:mm:ss A',
  'dddd, MMMM D HH:mm': 'dddd, MMMM D h:mm A',
  'dddd, MMMM D HH:mm:ss': 'dddd, MMMM D h:mm:ss A',
  'dddd, MMMM D, HH:mm:ss': 'dddd, MMMM D, h:mm:ss A',
  'L HH:mm': 'L h:mm A',
  'L HH:mm:ss': 'L h:mm:ss A',
  'LL, HH:mm:ss': 'LL, h:mm:ss A',
  'MMM D HH:mm': 'MMM D h:mm A',
  'll HH:mm': 'll h:mm A',
  'll HH:mm:ss': 'll h:mm:ss A',
  'll, HH:mm': 'll, h:mm A',
  'll, HH:mm:ss': 'll, h:mm:ss A',
  'DD/MM': 'MM/DD',
  'D. MMM, YYYY': 'MMM D, YYYY',
  'ddd, dd. MMM': 'ddd, MMM dd',
  'dd.MM.yyyy.': 'MM/dd/yyyy.',
  'ddd, DD.MM.': 'ddd, MM.DD.',
  'DD.MM.YYYY.': 'MM/DD/YYYY',
  'D. MMM': 'MMM D'
}

function getDateTimeFormatForUser(originalFormat: string) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return originalFormat

  return dateTimeFormatMap[originalFormat] || originalFormat
}

const unitMap: { [key: string]: string } = {
  'm/s²': 'ft/s²',
  'km/h': 'mph',
  'm/min': 'ft/min',
  'm/s': 'ft/s'
}

function getUnitSuffixForUser(originalUnit: string) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return originalUnit

  return unitMap[originalUnit] || originalUnit
}

function convertSpeed(value: number, originalUnit: string) {
  let result

  if (originalUnit === 'm/s') result = value * 3.6
  if (originalUnit === 'ft/s') result = value * 3.28 * 0.68 // first m/s to ft/s, then ft/s to mph
  if (originalUnit === 'km/h') result = value / 3.6
  if (originalUnit === 'mph') result = (value / 0.68) / 3.28 // first mph to ft/s, then ft/s to m/s

  if (!result) return value

  return result.toFixed(1)
}

function convertAccelerationToImperial(value: number, reversed: boolean = false) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return value

  if (reversed) return (value / 3.28).toFixed(1)

  return (value * 3.28).toFixed(1)
}

function getVariableNameWithAggregation(variable: string) {
  const VALID_AGGREGATIONS = ['DL', 'WL', 'WC', 'AL', 'CL', 'ACWR', 'FRES', 'MON', 'STR']

  const parts = variable.split('_')

  if (parts.length < 2) {
    return i18n.t(`variables.${variable}`)
  }

  const potentialAggregation = parts[parts.length - 1]

  if (VALID_AGGREGATIONS.includes(potentialAggregation)) {
    const varKey = parts.slice(0, -1).join('_')
    return `${i18n.t(`variables.${varKey}`)}: ${i18n.t(`variables.${potentialAggregation}.long`)}`
  }

  return i18n.t(`variables.${variable}`)
}

function getTranslatedVariableName(variableKey: string, useLong: boolean = true) {
  const suffix = useLong ? '.long' : ''
  return i18n.t(`variables.${variableKey}${suffix}`)
}

const EXERCISE_GROUP_NAMES: Record<string, string> = {
  STGH: 'Strength',
  FTB: 'Football',
  SPD: 'Speed',
  END: 'Endurance',
  PLY: 'Plyometrics',
  BAL: 'Ballistics',
  CRD: 'Cardio',
  MOB: 'Mobility',
  BSKT: 'Basketball',
  SWM: 'Swimming'
}

function getTranslatedTagValue(value: string) {
  // Check exercise group ID mapping first
  if (EXERCISE_GROUP_NAMES[value]) {
    return EXERCISE_GROUP_NAMES[value]
  }

  // Handle comma-separated values (from joined tag groups)
  if (value.includes(', ')) {
    return value.split(', ').map(v => {
      if (EXERCISE_GROUP_NAMES[v]) return EXERCISE_GROUP_NAMES[v]
      const key = toCamelCase(v)
      const translationKey = `training_exercise_tags.values.${key}`
      const translated = i18n.t(translationKey)
      return translated === translationKey ? v : translated
    }).join(', ')
  }

  const key = toCamelCase(value)
  const translationKey = `training_exercise_tags.values.${key}`
  const translated = i18n.t(translationKey)
  // If translation not found, return original value
  return translated === translationKey ? value : translated
}

export { getColorForValue, processMaxValues, processMinValues, getRandomInt, getColorForValueWithDefinition, toCamelCase, getOrdinal, calculateDistribution, splitVariable, adjustGridForTableHeights, ColorValueDefinition, getDateTimeFormatForUser, getUnitSuffixForUser, convertSpeed, convertAccelerationToImperial, getVariableNameWithAggregation, getTranslatedVariableName, getTranslatedTagValue }
